export const STACK_NAME_PATTERN = /at (.*?)(?: \(|\))/; // Regex pattern for extracting component/method names from stack traces

export const ERROR_CATEGORIES = {
  NETWORK: ['NetworkError', 'FetchError'],
  RUNTIME: ['ReferenceError', 'TypeError', 'SyntaxError', 'RangeError', 'URIError'],
  RENDERING: ['ReactError', 'ComponentError'],
  UNKNOWN: ['Error', 'UnknownError']
};

export const ERROR_CATEGORY_DISPLAY_NAMES = {
  NETWORK: 'Network Error',
  RUNTIME: 'Runtime Error',
  RENDERING: 'Rendering Error',
  UNKNOWN: 'Unknown Error'
};

// Categorizes an error based on its type
export const categorizeError = (error) => {
  if (!error || !error.name) {
    return 'UNKNOWN';
  }

  for (const [category, errorTypes] of Object.entries(ERROR_CATEGORIES)) {
    if (errorTypes.includes(error.name)) {
      return category;
    }
  }
  return 'UNKNOWN';
};

export const generateErrorId = (prefix = 'ERR_') => {
  return `${prefix}${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

export const generateErrorReportTitle = (errorId, type = 'Error Report') => {
  return `${type} - ${errorId}`;
};

export const formatErrorReport = ({ error, errorInfo, errorId, reportType = 'Error Report' }) => {
  let report = `${reportType} :- 
Error ID: ${errorId}
Error Type: ${error?.name || 'Unknown'}
Error Message: ${error?.message || 'No error message available'}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}`;

  // Add Component Name and Component Stack if available
  if (errorInfo?.componentStack) {
    const componentName = extractComponentName(errorInfo.componentStack);
    report += `\nComponent Name: ${componentName}`;
    report += `\n\nComponent Stack:\n${errorInfo.componentStack}`;
  }

  // Add Method Name and Stack Trace if available
  if (error?.stack) {
    const methodName = extractMethodName(error.stack);
    report += `\nMethod Name: ${methodName}`;
    report += `\n\nStack Trace:\n${error.stack}`;
  }

  return report;
};

// Extracts information from stack trace using regex pattern
export const extractFromStack = (stack, pattern, fallback) => {
  if (!stack) return fallback;
  const lines = stack.split('\n');
  for (const line of lines) {
    if (line.includes('at ')) {
      const match = line.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
  }
  return fallback;
};

// Extracts component name from component stack
export const extractComponentName = (componentStack) => {
  return extractFromStack(componentStack, STACK_NAME_PATTERN, 'Unknown Component');
};

// Extracts method name from error stack
export const extractMethodName = (errorStack) => {
  return extractFromStack(errorStack, STACK_NAME_PATTERN, 'Unknown Method');
};

export const getErrorCategoryDisplayName = (category) => {
  return ERROR_CATEGORY_DISPLAY_NAMES[category] || 'Unknown Error';
};

export const copyErrorToClipboard = async (errorReport) => {
  try {
    await navigator.clipboard.writeText(errorReport);
    return true;
  } catch (err) {
    console.error('Failed to copy error to clipboard:', err);
    return false;
  }
};

// Reports error via Freshdesk widget
export const reportError = (
  errorReport,
  errorReportTitle,
  isFreshworksWidgetAvailable,
  getCommonCustomFields
) => {
  if (isFreshworksWidgetAvailable()) {
    // Prefill the ticket form with error information
    FreshworksWidget('prefill', 'ticketForm', {
      subject: errorReportTitle,
      description: errorReport,
      custom_fields: getCommonCustomFields()
    });

    // Trigger the Freshdesk widget
    const iframe = document.getElementById('launcher-frame');
    if (iframe) {
      const innerDoc = iframe.contentDocument || iframe.contentWindow.document;
      const freshdeskClickable = innerDoc
        ? innerDoc.querySelector('[data-testid="launcher-wrapper"]')
        : null;
      if (freshdeskClickable?.click) {
        freshdeskClickable.click();
      }
    }
  }
};
